import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const LOGO_URL = "/logo.png";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Portfolio', path: '/portfolio' },
  { name: 'Contact', path: '/contact' },
];

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  return (
    <nav
      data-testid="global-header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 border-b border-slate-200 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <img
              src={LOGO_URL}
              alt="Ariana General Contracting"
              className="h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`text-sm font-semibold tracking-wide transition-colors duration-200 hover:text-[#00A2E8] ${
                  location.pathname === link.path ? 'text-[#00A2E8]' : 'text-slate-700'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA */}
          <Link
            to="/contact"
            data-testid="nav-get-quote-btn"
            className="hidden lg:block bg-[#00A2E8] hover:bg-[#0088C4] text-white px-6 py-3 font-bold text-sm tracking-widest transition-colors duration-200"
          >
            GET A QUOTE
          </Link>

          {/* Mobile hamburger */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-slate-700 hover:text-[#00A2E8] transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-slate-200 shadow-lg">
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col gap-1">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                className={`py-3 px-2 font-semibold text-sm tracking-wide border-b border-slate-100 transition-colors ${
                  location.pathname === link.path ? 'text-[#00A2E8]' : 'text-slate-700 hover:text-[#00A2E8]'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-get-quote-btn"
              className="mt-3 bg-[#00A2E8] text-white px-6 py-3 font-bold text-sm text-center tracking-widest hover:bg-[#0088C4] transition-colors"
            >
              GET A FREE QUOTE
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
